import { useState, type ReactNode } from "react";
import { Loader2, AlertTriangle } from "lucide-react";

import { formatDate, formatMoney } from "@/lib/format";
import type { ContractingReportRow } from "@/types/domain";
import { ContractingDetailSheet } from "@/components/contractings/ContractingDetailSheet";
import { StagePill } from "@/components/contractings/pills";
import {
  ContractingErrorFilters,
  type ErrorFiltersValue,
} from "@/components/admin/ContractingErrorFilters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** Una columna propia del reporte; van después de "Monto". */
export type ReportColumn<T> = {
  key: string;
  header: string;
  className?: string;
  render: (row: T) => ReactNode;
};

/**
 * Esqueleto de los reportes admin de licitaciones: cabecera con el conteo,
 * filtros, tabla y el sheet de detalle. Las columnas comunes (licitación, estado,
 * publicación, monto) las pone este componente; cada reporte agrega las suyas por
 * `columns`. La nomenclatura es el disparador del detalle: no hay columna de
 * acciones para un solo botón.
 */
export function ContractingReportTable<T extends ContractingReportRow>({
  title,
  description,
  filters,
  onFiltersChange,
  extraFilters,
  extraFiltersActive,
  rows,
  columns,
  isLoading,
  emptyMessage,
}: {
  title: string;
  description: string;
  filters: ErrorFiltersValue;
  onFiltersChange: (value: ErrorFiltersValue) => void;
  extraFilters?: ReactNode;
  /** Si los filtros propios del reporte están activos, para el mensaje de vacío. */
  extraFiltersActive?: boolean;
  rows: T[];
  columns: ReportColumn<T>[];
  isLoading: boolean;
  emptyMessage: string;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const hasFilters =
    filters.stages.length > 0 ||
    !!filters.publicatedFrom ||
    !!filters.publicatedTo ||
    !!extraFiltersActive;

  return (
    <div className="mx-auto w-full max-w-[2000px] flex-1 px-4 py-6 md:px-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">
          {description}
          {!isLoading &&
            ` ${rows.length} resultado${rows.length === 1 ? "" : "s"}.`}
        </p>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <ContractingErrorFilters value={filters} onChange={onFiltersChange} />
        {extraFilters}
      </div>

      <div className="rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <AlertTriangle className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? "Ninguna licitación coincide con estos filtros."
                : emptyMessage}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[360px]">Licitación</TableHead>
                  <TableHead className="w-px whitespace-nowrap">
                    Estado
                  </TableHead>
                  <TableHead className="w-px whitespace-nowrap">
                    Fecha de publicación
                  </TableHead>
                  <TableHead className="w-px whitespace-nowrap text-right">
                    Monto
                  </TableHead>
                  {columns.map((col) => (
                    <TableHead key={col.key} className={col.className}>
                      {col.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.contractingId}>
                    <TableCell className="min-w-[360px] max-w-[520px]">
                      <button
                        type="button"
                        onClick={() => setSelectedId(row.contractingId)}
                        title="Ver licitación"
                        className="block cursor-pointer text-left font-mono text-sm text-primary hover:underline"
                      >
                        {row.nomenclature}
                      </button>
                      <span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {row.description}
                      </span>
                      <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground/70">
                        {row.location || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="w-px">
                      <StagePill stage={row.stage} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(row.publicatedAt)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                      {formatMoney(row.amount, row.currencySymbol)}
                    </TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.className}>
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ContractingDetailSheet
        contractingId={selectedId}
        open={selectedId != null}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
    </div>
  );
}
